// Month ↔ X-offset helpers for the Market Research city trend charts.
import { canonicalizeDataMonth } from "@shared/market-research-canonical";
//
// Month labels are stored as "Month YYYY" strings (e.g. "March 2025",
// "September 2026"). The chart's numeric X-offset is derived from the label
// itself so any newly-added month automatically appears on the chart
// without a code change. March 2025 is offset 0 (the original baseline).

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export type ResearchChartRange = "all" | 24 | 12 | 6;

/** Calendar-month window ending at the newest available month, not today.
 * Missing months remain gaps; "6 months" does not mean six collected points.
 */
export function filterChartMonthRange<T extends { monthOffset: number }>(
  points: readonly T[],
  range: ResearchChartRange,
): T[] {
  const valid = points.filter(point => Number.isFinite(point.monthOffset));
  if (range === "all" || valid.length === 0) return valid;
  const newest = Math.max(...valid.map(point => point.monthOffset));
  return valid.filter(point => point.monthOffset >= newest - range + 1);
}

export function monthLabelToOffset(label: string | null | undefined): number | undefined {
  if (!label) return undefined;
  const parts = label.trim().split(/\s+/);
  if (parts.length !== 2) return undefined;
  // Canonical labels use the full month name ("July 2026"), but be
  // tolerant of abbreviated forms ("Jul 2026", "Sept 2026") and
  // 2-digit years ("July 26") so a row written by an older import
  // still lands on the chart instead of silently disappearing.
  const monthToken = parts[0].toLowerCase().replace(/[.,]/g, "");
  const monthIdx = monthToken.length >= 3
    ? MONTH_NAMES.findIndex((name) => name.toLowerCase().startsWith(monthToken))
    : -1;
  let year = parseInt(parts[1], 10);
  if (year >= 0 && year < 100) year += 2000;
  if (monthIdx < 0 || Number.isNaN(year)) return undefined;
  // March 2025 (year=2025, monthIdx=2) → 0
  return (year - 2025) * 12 + monthIdx - 2;
}

export function offsetToMonthLabel(offset: number): string | undefined {
  if (!Number.isFinite(offset)) return undefined;
  const total = offset + 2; // shift back so March 2025 (offset 0) → total=2
  const year = 2025 + Math.floor(total / 12);
  const m = ((total % 12) + 12) % 12;
  return `${MONTH_NAMES[m]} ${year}`;
}

// Pixel padding on both ends of the X axis. The tick label is centered on
// the first/last month, so without this the year text ("2026") is clipped
// at the chart edge. Pixel (not month-unit) padding keeps the room constant
// however many months the chart spans.
export const MONTH_AXIS_EDGE_PADDING_PX = 24;

// Numeric time axes must retain calendar spacing. As months accumulate,
// scroll rather than letting Recharts silently suppress adjacent month ticks.
export function monthChartMinWidth(offsets: readonly number[]): number {
  const [lo, hi] = monthAxisDomain(offsets);
  return Math.max(320, (hi - lo) * 56 + 160);
}

/** Collapse legacy month aliases without discarding differing stored rows. */
export function canonicalHistoryMonths<T extends { month: string }>(rows: readonly T[]): T[] {
  const months = new Map<string, T>();
  for (const row of rows) {
    const month = canonicalizeDataMonth(row.month);
    if (!month) continue;
    if (!months.has(month) || row.month === month) months.set(month, { ...row, month });
  }
  return Array.from(months.values());
}

/**
 * X-axis domain spanning exactly the months present. Replaces the old
 * hardcoded [-0.5, 13.5] (which ended at April 2026 and pushed every newer
 * month onto the clipped right edge). Edge room comes from
 * MONTH_AXIS_EDGE_PADDING_PX, not from the domain.
 */
export function monthAxisDomain(offsets: readonly number[]): [number, number] {
  const finite = offsets.filter((o) => Number.isFinite(o));
  if (finite.length === 0) return [0, 1];
  const min = Math.min(...finite);
  const max = Math.max(...finite);
  return min === max ? [min - 1, max + 1] : [min, max];
}

const TREND_MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Google Trends date ("Sep 2026", "Sep 1 – 7, 2026") → chart offset, or NaN. */
export function trendsDateToMonthOffset(dateStr: string): number {
  const parts = dateStr.trim().split(/\s+/);
  if (parts.length >= 2) {
    const monthIdx = TREND_MONTH_NAMES.indexOf(parts[0].substring(0, 3));
    const year = parseInt(parts[parts.length - 1]);
    if (monthIdx >= 0 && !isNaN(year)) {
      return (year - 2025) * 12 + (monthIdx - 2);
    }
  }
  return NaN;
}

/**
 * Average Google Trends points into one value per month offset, keeping
 * only months inside the PadSplit history range so the overlay covers every
 * month on the chart without stretching the axis past the data. With no
 * history, every parseable non-negative month is kept.
 */
export function bucketTrendsByMonth(
  points: ReadonlyArray<{ date: string; value: number }>,
  historyOffsets: readonly number[],
): Map<number, number> {
  const finite = historyOffsets.filter((o) => Number.isFinite(o));
  const lo = finite.length ? Math.min(...finite) : 0;
  const hi = finite.length ? Math.max(...finite) : Infinity;
  const map = new Map<number, number>();
  for (const pt of points) {
    const offset = trendsDateToMonthOffset(pt.date);
    if (Number.isNaN(offset) || offset < lo || offset > hi) continue;
    const existing = map.get(offset);
    map.set(offset, existing === undefined ? pt.value : Math.round((existing + pt.value) / 2));
  }
  return map;
}
